import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStore } from "../store/useStore";

interface ChangeEvent {
  kind: "layer" | "field" | "form" | "feature" | "project" | "imbiss" | "attachment" | "maengel" | "meldungen" | "routen" | "ping";
  layerId?: string;
  projectId?: string;
  featureId?: string;
}

/**
 * Subscribe to the backend SSE stream and invalidate the relevant TanStack
 * queries on every config/data change — so an admin's edit to a form, field,
 * style or feature reflects on every open map (any user) within a frame, no
 * reload. Reconnects automatically (EventSource built-in).
 */
export function useRealtimeSync(): void {
  const qc = useQueryClient();
  const auth = useStore((s) => s.auth);
  useEffect(() => {
    if (!auth) return; // SSE endpoint is session-authenticated
    // Demo: die Ueberlagerung lebt im Browser und ueberlebt jede Invalidierung —
    // ein Ereignisstrom brauchte sie nicht und hielte je Gast eine Verbindung offen.
    if (auth.demo) return;
    const es = new EventSource("/api/events", { withCredentials: true });
    es.onmessage = (e) => {
      let ev: ChangeEvent;
      try {
        ev = JSON.parse(e.data) as ChangeEvent;
      } catch {
        return;
      }
      switch (ev.kind) {
        case "imbiss":
          // 🍔 live collaboration: a trio member's rating refreshes everyone within a frame
          void qc.invalidateQueries({ queryKey: ["imbiss"] });
          void qc.invalidateQueries({ queryKey: ["imbiss-board"] });
          void qc.invalidateQueries({ queryKey: ["imbiss-search"] });
          void qc.invalidateQueries({ queryKey: ["imbiss-area"] });
          break;
        case "form":
          if (ev.layerId) void qc.invalidateQueries({ queryKey: ["form", ev.layerId] });
          break;
        case "field":
          if (ev.layerId) {
            void qc.invalidateQueries({ queryKey: ["columns", ev.layerId] });
            void qc.invalidateQueries({ queryKey: ["vr-options", ev.layerId] });
          }
          if (ev.projectId) void qc.invalidateQueries({ queryKey: ["valuerel", ev.projectId] });
          break;
        case "layer":
          if (ev.projectId) void qc.invalidateQueries({ queryKey: ["layers", ev.projectId] });
          if (ev.layerId) void qc.invalidateQueries({ queryKey: ["columns", ev.layerId] });
          break;
        case "project":
          // visibility / basemap / origin / name / thumbnail — reflect everywhere live
          void qc.invalidateQueries({ queryKey: ["projects"] });
          if (ev.projectId) {
            void qc.invalidateQueries({ queryKey: ["project", ev.projectId] });
            void qc.invalidateQueries({ queryKey: ["stats", ev.projectId] });
            void qc.invalidateQueries({ queryKey: ["layers", ev.projectId] });
          }
          break;
        case "feature":
          if (ev.layerId) {
            void qc.invalidateQueries({ queryKey: ["feature", ev.layerId] });
            // Objektliste NICHT ueber den Praefix: die Dashboard-Endlosliste fuehrt
            // JE SEITE einen eigenen Eintrag (["objektliste", ebene, filter, seite]),
            // und jede Seite ist ein aktiver Beobachter. Ein Praefixtreffer laedt
            // bei zehn geladenen Seiten zehn Abfragen gleichzeitig nach — bei einem
            // Import, der je Zeile ein Ereignis schickt, im Sekundentakt. Nur als
            // veraltet MARKIEREN: die sichtbare Seite holt beim naechsten Blick
            // nach, die uebrigen erst, wenn sie wieder gebraucht werden.
            void qc.invalidateQueries({ queryKey: ["objektliste", ev.layerId], refetchType: "none" });
            if (ev.projectId) void qc.invalidateQueries({ queryKey: ["stats", ev.projectId] });
          }
          break;
        /**
         * Anhaenge und Maengel sendet das Backend seit jeher (attachments.controller,
         * maengel.controller) — hier fielen sie bis eben in `default: break`. Damit
         * erreichte ein gesetzter Stern, ein neues Foto oder ein erledigter Mangel
         * nicht einmal MIT Ereignisstrom einen zweiten Reiter oder Browser.
         *
         * Die Nutzlast traegt nur die Ebene, kein Objekt — deshalb der Praefix
         * ["attachments", ebene]: er trifft jeden Leser dieser Ebene (Fotos-Reiter,
         * Steckbrief, Karten-Tooltip, Fotozellen der Objektliste) und keinen einer
         * anderen.
         */
        case "attachment":
          if (ev.layerId) void qc.invalidateQueries({ queryKey: ["attachments", ev.layerId] });
          break;
        case "maengel":
          if (ev.layerId) {
            void qc.invalidateQueries({ queryKey: ["maengel", ev.layerId] });
            // der Trigger setzt die Merkmalspalte — die Trefferlisten aendern sich mit
            void qc.invalidateQueries({ queryKey: ["objektliste", ev.layerId] });
            // Mängel-Reiter: Auswertung und Liste rechnen über dieselben Zeilen
            void qc.invalidateQueries({ queryKey: ["maengel-stats"] });
            void qc.invalidateQueries({ queryKey: ["maengel-liste"] });
          }
          // offene Maengel mit Dringlichkeit Gefahr speisen die Hinweisschicht
          void qc.invalidateQueries({ queryKey: ["hinweise"] });
          break;
        case "meldungen":
          // neue oder bearbeitete Buergermeldung: Reiter und Hinweisschicht
          void qc.invalidateQueries({ queryKey: ["meldungen"] });
          void qc.invalidateQueries({ queryKey: ["hinweise"] });
          break;
        case "routen":
          // jemand hat eine Route angelegt, geaendert oder uebernommen
          void qc.invalidateQueries({ queryKey: ["routen"] });
          break;
        default:
          break; // ping / unknown
      }
    };
    es.onerror = () => {
      /* EventSource auto-reconnects; nothing to do */
    };
    return () => es.close();
  }, [auth, qc]);
}
